import { useTranslation } from "react-i18next";
import { Button, Icon, cn } from "@nexus/ui";
import type { UploadItem } from "../../hooks/useUploadManager";
import { formatAssetDisplayName } from "../../lib/format-asset-name";
import { formatAssetModifiedDate } from "../../lib/format-asset-date";
import { formatFileSize } from "../../lib/format-file-size";
import { UploadAssetPreview } from "./UploadAssetPreview";

/** Small determinate progress bar — `@nexus/ui` has no generic Progress primitive (KTD3). */
function UploadProgressBar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));
  return (
    <div
      className="bg-muted h-1 w-full overflow-hidden rounded-full"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="bg-primary h-full rounded-full transition-[width] duration-300 ease-out"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function uploadMetaLine(
  item: UploadItem,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const size = formatFileSize(item.sizeBytes ?? item.file.size);
  const date = formatAssetModifiedDate(item.file.lastModified);

  if (item.state === "uploading") {
    return t("preflight.assetList.uploadingMeta", {
      size,
      percent: Math.round(item.progress),
    });
  }

  if (item.state === "failed" && item.error) {
    return t("preflight.assetList.failedMeta", { size, error: item.error });
  }

  return t("preflight.assetList.fileMeta", {
    size,
    date,
    status: t(`preflight.uploadState.${item.state}`),
  });
}

interface FileUploadRowProps {
  item: UploadItem;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}

function FileUploadRow({ item, onCancel, onRetry, onRemove }: FileUploadRowProps) {
  const { t } = useTranslation();
  const isInFlight = item.state === "uploading" || item.state === "confirming";
  const displayName = formatAssetDisplayName(item.file.name);
  const meta = uploadMetaLine(item, t);

  return (
    <li
      className={cn(
        "flex flex-col gap-2 rounded-md border px-3 py-2.5",
        item.state === "failed"
          ? "border-error/40 bg-error-subtle/20"
          : "border-border bg-card",
      )}
    >
      <div className="flex items-start gap-3">
        <UploadAssetPreview
          file={item.file}
          name={item.file.name}
          state={item.state}
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-foreground truncate text-sm font-medium" title={item.file.name}>
            {displayName}
          </p>
          <p
            className={cn(
              "mt-0.5 truncate text-xs",
              item.state === "failed" ? "text-error" : "text-muted-foreground",
            )}
            title={meta}
          >
            {meta}
          </p>
        </div>
        {isInFlight ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-primary hover:text-primary min-h-11 shrink-0 px-2 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"
            onClick={() => onCancel(item.id)}
            aria-label={t("preflight.assetList.cancelUpload", {
              name: item.file.name,
            })}
          >
            {t("preflight.assetList.cancel")}
          </Button>
        ) : item.state === "failed" ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"
              iconStart="refresh-cw"
              onClick={() => onRetry(item.id)}
              aria-label={t("preflight.assetList.retryUpload", {
                name: item.file.name,
              })}
            >
              {t("preflight.assetList.retry")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
              iconStart="trash-2"
              onClick={() => onRemove(item.id)}
              aria-label={t("preflight.assetList.remove", {
                name: item.file.name,
              })}
            />
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground min-h-11 min-w-11 shrink-0 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
            iconStart="trash-2"
            onClick={() => onRemove(item.id)}
            aria-label={t("preflight.assetList.remove", {
              name: item.file.name,
            })}
          />
        )}
      </div>
      {isInFlight ? <UploadProgressBar percent={item.progress} /> : null}
    </li>
  );
}

interface UrlRowProps {
  url: string;
  onRemove: (url: string) => void;
}

function UrlRow({ url, onRemove }: UrlRowProps) {
  const { t } = useTranslation();

  return (
    <li className="border-border bg-card flex items-start gap-3 rounded-md border px-3 py-2.5">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:bg-muted/40 focus-visible:ring-ring/40 -mx-1 flex min-w-0 flex-1 items-start gap-3 rounded-md px-1 py-0.5 transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
        aria-label={t("common.openExternal")}
      >
        <span
          className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md ring-1 ring-border/60"
          aria-hidden
        >
          <Icon name="external-link" size="sm" />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <p
            className="text-primary truncate text-sm font-medium underline-offset-2 hover:underline"
            title={url}
          >
            {url}
          </p>
          <p className="text-muted-foreground mt-0.5 truncate text-xs">
            {t("preflight.assetList.urlMeta")}
          </p>
        </div>
      </a>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground hover:text-foreground shrink-0"
        iconStart="trash-2"
        onClick={() => onRemove(url)}
        aria-label={t("preflight.assetList.removeUrl", { url })}
      />
    </li>
  );
}

export interface AssetListProps {
  items: UploadItem[];
  urls: string[];
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onRemoveItem: (id: string) => void;
  onRemoveUrl: (url: string) => void;
}

/**
 * Renders queued/in-flight/ready file uploads (with per-file progress + cancel/
 * retry/remove) and pasted URL chips (Hub R2/R3).
 */
export function AssetList({
  items,
  urls,
  onCancel,
  onRetry,
  onRemoveItem,
  onRemoveUrl,
}: AssetListProps) {
  const { t } = useTranslation();
  if (items.length === 0 && urls.length === 0) return null;

  return (
    <ul className="flex flex-col gap-2" aria-label={t("preflight.assetList.selected")}>
      {items.map((item) => (
        <FileUploadRow
          key={item.id}
          item={item}
          onCancel={onCancel}
          onRetry={onRetry}
          onRemove={onRemoveItem}
        />
      ))}
      {urls.map((url) => (
        <UrlRow key={url} url={url} onRemove={onRemoveUrl} />
      ))}
    </ul>
  );
}
